import { h as __h } from "preact";
import { signal } from "@preact/signals";
/** @import { Signal } from "@preact/signals"; */
import { Component, h } from "preact";
import { adminMachineResponse, locationResponse, relayResponse } from "~/backend/validate.js";
import { query, paginatedResponse, response } from "~/lib/api.js";
import { identity } from "~/lib/identity.js";
import * as msg from "~/lib/ws/msg.js";
/** @import WS from "~/lib/ws/ws.js"; */

import html from "~/util/html.js";
import { pluralize } from "~/util/pluralize.js";
import { navigate } from "~/util/router.js";
import Title from "~/util/title.js";
import Button from "~/component/Button.js";
import Data from "~/component/Data.js";
import Icon from "~/component/Icon.js";
import Loader from "~/component/Loader.js";
import Poller from "~/component/Poller.js";
import WSListener from "~/component/WSListener.js";
import { Crumb } from "~/admin/component/Breadcrumbs.js";
import FreshKeyCallout from "~/admin/component/FreshKeyCallout.js";
/** @import { FreshKey } from "~/admin/component/FreshKeyCallout.js"; */
import ObjectPage, { ObjectPageSection } from "~/admin/component/ObjectPage.js";
import Pagination from "~/admin/component/Pagination.js";
import Table from "~/admin/component/Table.js";
import ComputerTable from "~/admin/res/computer/ComputerTable.js";
import { deleteLocation } from "~/admin/res/location/actions/deleteLocation.js";
import { renameLocation } from "~/admin/res/location/actions/renameLocation.js";
import { addRelay } from "~/admin/res/relay/actions/addRelay.js";
import { downloadRelay } from "~/admin/res/relay/actions/downloadRelay.js";
import RelayTable from "~/admin/res/relay/RelayTable.js";
import util from "~/style/util.js";
/** @import { orgResponse } from "~/backend/validate.js"; */

/**
 * @extends {Component<Props>}
 *
 * @typedef {object} Props
 * @property {orgResponse} org
 * @property {string} locationId
 * @property {WS} ws
 */
export default class LocationsDetail extends Component {
  locationLookup = query(() => ({
    key: [`/v2/orgs/${this.props.org.id}/locations/${this.props.locationId}`],
    keepPreviousData: true
  }), {
    schema: response(locationResponse)
  });
  freshKey = signal(/** @type {FreshKey | null} */null);
  render() {
    const location = this.locationLookup.data?.data;
    return [__h(Crumb, {}, location?.name ?? __h(Loader, {})), location && __h(Title, {
      title: location.name
    }), __h(Data, {
      loading: this.locationLookup.loading && !this.locationLookup.data,
      error: this.locationLookup.error,
      empty: Boolean(this.locationLookup.data) && !location
    }, location && this.renderPage(location))];
  }

  /** @param {locationResponse} location */
  renderPage(location) {
    const canEditLocations = identity.permission(this.props.org.id, "locations", "edit");
    const canEditRelays = identity.permission(this.props.org.id, "relays", "edit");
    return __h(ObjectPage, {
      backlink: {
        href: `/org/${this.props.org.id}/relays`,
        name: "Locations"
      },
      title: [__h(Icon, {
        name: "antenna",
        size: 22
      }), __h("span", {}, location.name)],
      desc: location.description,
      actions: [canEditRelays && __h(Button, {
        level: "primary",
        size: "small",
        onClick: async () => {
          const res = await addRelay({
            orgId: this.props.org.id
          }, location);
          if (res) this.freshKey.value = res;
        }
      }, __h(Icon, {
        name: "plus",
        size: 12
      }), "Add relay"), canEditLocations && [__h(Button, {
        size: "small",
        onClick: () => renameLocation({
          orgId: this.props.org.id
        }, location)
      }, __h(Icon, {
        name: "pencil",
        size: 12
      }), "Rename location"), __h(Button, {
        size: "small",
        kind: "negative",
        disabled: location.relay_count > 0,
        title: location.relay_count > 0 ? "This location still contains relays" : undefined,
        onClick: async () => {
          const res = await deleteLocation({
            orgId: this.props.org.id
          }, location);
          if (res) navigate(`/org/${this.props.org.id}/relays`);
        }
      }, __h(Icon, {
        name: "trash",
        size: 12
      }), "Delete location")], __h(Button, {
        size: "small",
        onClick: () => downloadRelay()
      }, __h(Icon, {
        name: "download-binary",
        size: 12
      }), "Download binary")],
      sidebar: [__h(ObjectPageSection, {
        title: "Relays"
      }, __h("span", {}, location.relay_count, " ", pluralize(location.relay_count, "relay"))), __h(ObjectPageSection, {
        title: "Computers"
      }, __h("span", {}, location.machine_count, " ", pluralize(location.machine_count, "computer")))]
    }, __h(FreshKeyCallout, {
      freshKey: this.freshKey.value
    }), __h(ObjectPageSection, {
      title: "Relays"
    }, __h("div", {
      "class": util.card
    }, __h(Relays, {
      org: this.props.org,
      locationId: location.id,
      freshKey: this.freshKey
    }))), __h(ObjectPageSection, {
      title: "Computers"
    }, __h("div", {
      "class": util.card
    }, __h(Computers, {
      org: this.props.org,
      locationId: location.id,
      ws: this.props.ws
    }))));
  }
}
/**
 * @extends {Component<RelaysProps>}
 *
 * @typedef {object} RelaysProps
 * @property {orgResponse} org
 * @property {string} locationId
 * @property {Signal<FreshKey | null>} freshKey
 */
class Relays extends Component {
  static DURATION = 3000;
  paginate = new Pagination.State({
    size: 5,
    local: true
  });
  sort = new Table.Sort({
    keys: ["name"],
    by: "name",
    local: true
  });
  relays = query(() => ({
    key: [`/v2/orgs/${this.props.org.id}/relays`, {
      ...this.paginate.query(),
      ...this.sort.query(),
      location_id: this.props.locationId
    }],
    keepPreviousData: true
  }), {
    schema: paginatedResponse(relayResponse)
  });
  render() {
    return [__h(Poller, {
      poll: () => this.relays.fetch().catch(() => {}),
      duration: Relays.DURATION
    }), h(RelayTable, {
      orgId: this.props.org.id,
      paginate: this.paginate,
      sort: this.sort,
      freshKey: this.props.freshKey,
      query: this.relays,
      scroll: false
    })];
  }
}

/**
 * @extends {Component<ComputersProps>}
 *
 * @typedef {object} ComputersProps
 * @property {orgResponse} org
 * @property {string} locationId
 * @property {WS} ws
 */
class Computers extends Component {
  paginate = new Pagination.State({
    size: 5,
    local: true
  });
  sort = ComputerTable.sort({
    local: true
  });
  machines = query(() => ({
    key: [`/ui/admin/orgs/${this.props.org.id}/machines`, {
      ...this.paginate.query(),
      ...this.sort.query(),
      location_id: this.props.locationId
    }],
    keepPreviousData: true
  }), {
    schema: paginatedResponse(adminMachineResponse)
  });
  render() {
    const locationId = this.props.locationId;
    return [h(ComputerTable, {
      orgId: this.props.org.id,
      paginate: this.paginate,
      sort: this.sort,
      query: this.machines,
      headers: h => h.filter(h => h.id !== "location"),
      footer: __h("a", {
        href: "/org/" + this.props.org.id + "/computers?location_id=" + locationId
      }, "Show/edit all results"),
      scroll: false
    }), __h(WSListener, {
      ws: this.props.ws,
      "for": [msg.ADMIN_MACHINE_REFRESH, msg.ADMIN_CONNECTION_REFRESH],
      on: () => this.machines.fetch()
    })];
  }
}